import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FolderOpen, Upload, Download, Trash2, Search, FileText,
  FileImage, FileSpreadsheet, FileArchive, File as FileIcon, Loader2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', label: 'All Files' },
  { id: 'po', label: 'POs' },
  { id: 'agreement', label: 'Agreements' },
  { id: 'kyc', label: 'KYC' },
  { id: 'invoice', label: 'Invoices' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'other', label: 'Other' },
];

const SIZE_FMT = (n) => {
  if (n == null) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fileIcon = (ct = '') => {
  if (ct.startsWith('image/')) return FileImage;
  if (ct.includes('sheet') || ct.includes('csv')) return FileSpreadsheet;
  if (ct.includes('zip') || ct.includes('compressed')) return FileArchive;
  if (ct.includes('pdf') || ct.includes('text')) return FileText;
  return FileIcon;
};

export default function FileManager() {
  const [searchParams] = useSearchParams();
  const customerFilter = searchParams.get('customer_id') || '';
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState({ total_vault_files: 0, total_customer_files: 0, by_category: {} });
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ category: 'other', description: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (q) params.append('q', q);
      if (customerFilter) params.append('customer_id', customerFilter);
      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/files/list?${params.toString()}`, { headers }),
        fetch(`${API}/api/files/stats`, { headers }),
      ]);
      const listData = await listRes.json();
      const statsData = await statsRes.json();
      setFiles(listData.files || []);
      setStats(statsData || {});
    } catch (e) {
      toast.error('Failed to load files');
    }
    setLoading(false);
  }, [category, q, customerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Choose a file'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('category', uploadForm.category);
      fd.append('description', uploadForm.description || '');
      if (customerFilter) fd.append('customer_id', customerFilter);
      const res = await fetch(`${API}/api/files/upload`, {
        method: 'POST',
        headers,
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('File uploaded');
        setShowUpload(false);
        setUploadFile(null);
        setUploadForm({ category: 'other', description: '' });
        load();
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch { toast.error('Network error'); }
    setUploading(false);
  };

  const handleDownload = async (f) => {
    try {
      const res = await fetch(`${API}/api/files/${f.file_id}/download`, { headers });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = f.display_name || f.original_filename || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download error'); }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.display_name}"? (soft-delete)`)) return;
    try {
      const res = await fetch(`${API}/api/files/${f.file_id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('File deleted'); load(); }
      else { const d = await res.json(); toast.error(d.detail || 'Delete failed'); }
    } catch { toast.error('Network error'); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="file-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FolderOpen className="h-6 w-6" /> File Management Center
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {customerFilter
              ? `Per-customer attachments • customer_id=${customerFilter}`
              : 'Admin document vault + per-customer attachments'}
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="file-upload-btn">
          <Upload className="h-4 w-4 mr-2" /> Upload File
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Vault Files</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_vault_files || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Per-Customer Files</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_customer_files || 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Categories</p>
            <p className="text-2xl font-bold text-slate-900">{Object.keys(stats.by_category || {}).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500">Total Vault Size</p>
            <p className="text-2xl font-bold text-slate-900">
              {SIZE_FMT(Object.values(stats.by_category || {}).reduce((a, b) => a + (b.size || 0), 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                category === c.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`file-cat-${c.id}`}
            >
              {c.label}
              {stats.by_category?.[c.id]?.count ? (
                <Badge variant="secondary" className="ml-2 text-xs">{stats.by_category[c.id].count}</Badge>
              ) : null}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search filename, description..."
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9"
            data-testid="file-search-input"
          />
        </div>
      </div>

      {/* File List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Files ({files.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : files.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <FolderOpen className="mx-auto h-10 w-10 text-slate-300 mb-2" />
              <p>No files yet</p>
              <p className="text-xs mt-1">Upload your first document to get started</p>
            </div>
          ) : (
            <div className="divide-y">
              {files.map(f => {
                const Icon = fileIcon(f.content_type);
                return (
                  <div key={f.file_id} className="p-4 flex items-center gap-4 hover:bg-slate-50" data-testid={`file-row-${f.file_id}`}>
                    <Icon className="h-8 w-8 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-900 truncate">{f.display_name || f.original_filename}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-slate-500">
                        <Badge variant="outline" className="uppercase">{f.category}</Badge>
                        <span>{SIZE_FMT(f.size)}</span>
                        <span>•</span>
                        <span>{new Date(f.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {f.uploaded_by && <><span>•</span><span>by {f.uploaded_by}</span></>}
                      </div>
                      {f.description && <p className="text-xs text-slate-500 mt-1 truncate">{f.description}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleDownload(f)} title="Download" data-testid={`file-download-${f.file_id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(f)} title="Delete" data-testid={`file-delete-${f.file_id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent data-testid="file-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>Max 50MB per file. Stored in Emergent Object Storage.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File</Label>
              <Input
                ref={fileInputRef}
                type="file"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                data-testid="file-upload-input"
              />
              {uploadFile && (
                <p className="text-xs text-slate-500 mt-1">
                  {uploadFile.name} ({SIZE_FMT(uploadFile.size)})
                </p>
              )}
            </div>
            <div>
              <Label>Category</Label>
              <Select value={uploadForm.category} onValueChange={v => setUploadForm(p => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="file-upload-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Notes about this document..."
                value={uploadForm.description}
                onChange={e => setUploadForm(p => ({ ...p, description: e.target.value }))}
                rows={3}
                data-testid="file-upload-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile} data-testid="file-upload-confirm">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
